import React, { useState, useEffect } from 'react';
import { Zap, Search, Filter, FileText, Clock, DollarSign, Tag } from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import toast from 'react-hot-toast';
import { extractTenantSubdomain } from '@/utils/tenantHostname';

interface Workflow {
  id: string;
  country: string;
  name: string;
  description: string;
  type: string;
  priority: string;
  estimatedHours?: number;
  billing?: {
    feeType: string;
    fixedAmount?: number;
    billableRate?: number;
  };
  tags: string[];
  checklist?: Array<{
    label: string;
    description?: string;
    required?: boolean;
    order?: number;
  }>;
  recurrence?: {
    isRecurring: boolean;
    frequency?: string;
    interval?: number;
  };
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const CountryWorkflows: React.FC = () => {
  const { tenant } = useAuthStore();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const countries = [
    { code: 'canada', label: 'Canada' },
    { code: 'india', label: 'India' },
    { code: 'usa', label: 'USA' },
    { code: 'uae', label: 'UAE' },
    { code: 'germany', label: 'Germany' },
    { code: 'france', label: 'France' },
    { code: 'australia', label: 'Australia' },
  ];

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const headers: Record<string, string> = {};
      const hostSub = extractTenantSubdomain(window.location.hostname);
      if (tenant?.subdomain) headers['X-Firm'] = tenant.subdomain;
      if (hostSub) headers['X-Firm'] = hostSub;
      if (tenant?.id) headers['X-Tenant'] = tenant.id;

      const response = await api.get('/firm-admin/workflows/country-templates', { headers });
      setWorkflows(response.data.data || []);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      const resp = (error as any)?.response;
      const message =
        resp?.data?.error?.message || resp?.data?.message || resp?.statusText || 'Failed to load workflows';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getCountryLabel = (code: string) => {
    return countries.find((c) => c.code === code)?.label || code;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toUpperCase()) {
      case 'URGENT':
        return 'bg-red-100 text-red-800';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type: string) => {
    const iconProps = 'w-4 h-4';
    switch (type?.toUpperCase()) {
      case 'TAX_RETURN':
        return <FileText className={iconProps} />;
      case 'BOOKKEEPING':
        return <FileText className={iconProps} />;
      case 'PAYROLL':
        return <DollarSign className={iconProps} />;
      default:
        return <FileText className={iconProps} />;
    }
  };

  const filteredWorkflows = workflows.filter((w) => {
    const matchesSearch =
      w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCountry = !selectedCountry || w.country === selectedCountry;

    return matchesSearch && matchesCountry;
  });

  const groupedByCountry = selectedCountry
    ? filteredWorkflows
    : filteredWorkflows.reduce(
        (acc, workflow) => {
          if (!acc[workflow.country]) {
            acc[workflow.country] = [];
          }
          acc[workflow.country].push(workflow);
          return acc;
        },
        {} as Record<string, Workflow[]>
      );

  return (
    <div className="flex-1 overflow-auto bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Zap className="w-8 h-8 text-primary-500 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Workflow Templates</h1>
              <p className="text-sm text-gray-600 mt-1">
                Browse and reference country-specific workflows for your accounting practice
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-3" />
            <input
              type="text"
              placeholder="Search workflows..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Country Filter */}
          <select
            value={selectedCountry || ''}
            onChange={(e) => setSelectedCountry(e.target.value || null)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Countries</option>
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.label} ({workflows.filter((w) => w.country === country.code).length})
              </option>
            ))}
          </select>

          {/* Results Counter */}
          <div className="flex items-center px-4 py-2 bg-gray-50 rounded-lg">
            <Filter className="w-5 h-5 text-gray-600 mr-2" />
            <span className="text-sm text-gray-700">
              {filteredWorkflows.length} workflow{filteredWorkflows.length !== 1 ? 's' : ''} found
            </span>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
            <p className="text-gray-600">Loading workflows...</p>
          </div>
        </div>
      )}

      {/* Workflows Display */}
      {!loading && filteredWorkflows.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No workflows found matching your criteria</p>
        </div>
      ) : (
        selectedCountry ? (
          // Single Country View
          <div className="space-y-4">
            <div className="flex items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {getCountryLabel(selectedCountry)} Workflows
              </h2>
              <span className="ml-2 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                {filteredWorkflows.length}
              </span>
            </div>

            {filteredWorkflows.map((workflow) => (
              <WorkflowCard
                key={workflow.id}
                workflow={workflow}
                onSelect={() => {
                  setSelectedWorkflow(workflow);
                  setShowDetails(true);
                }}
              />
            ))}
          </div>
        ) : (
          // All Countries View (Grouped)
          <div className="space-y-6">
            {Object.entries(groupedByCountry).map(([country, countryWorkflows]) => (
              <div key={country}>
                <div className="flex items-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {getCountryLabel(country)}
                  </h2>
                  <span className="ml-2 px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm font-medium">
                    {countryWorkflows.length}
                  </span>
                </div>

                <div className="space-y-3">
                  {countryWorkflows.map((workflow) => (
                    <WorkflowCard
                      key={workflow.id}
                      workflow={workflow}
                      onSelect={() => {
                        setSelectedWorkflow(workflow);
                        setShowDetails(true);
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Workflow Details Modal */}
      {showDetails && selectedWorkflow && (
        <WorkflowDetailsModal
          workflow={selectedWorkflow}
          onClose={() => {
            setShowDetails(false);
            setSelectedWorkflow(null);
          }}
        />
      )}
    </div>
  );
};

const WorkflowCard: React.FC<{
  workflow: Workflow;
  onSelect: () => void;
}> = ({ workflow, onSelect }) => {
  const getPriorityColor = (priority: string) => {
    switch (priority?.toUpperCase()) {
      case 'URGENT':
        return 'bg-red-100 text-red-800';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <button
      onClick={onSelect}
      className="w-full text-left bg-white rounded-lg shadow-sm hover:shadow-md p-4 transition-shadow border border-gray-200 hover:border-primary-300"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-sm font-semibold text-gray-900">{workflow.name}</h3>
            {workflow.priority && (
              <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(workflow.priority)}`}>
                {workflow.priority}
              </span>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{workflow.description}</p>

          <div className="flex flex-wrap gap-2 items-center">
            {workflow.estimatedHours && (
              <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                <Clock className="w-3 h-3 mr-1" />
                {workflow.estimatedHours}h
              </div>
            )}

            {workflow.billing?.fixedAmount && (
              <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                <DollarSign className="w-3 h-3 mr-1" />
                ${workflow.billing.fixedAmount}
              </div>
            )}

            {workflow.tags.slice(0, 2).map((tag) => (
              <div key={tag} className="flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                <Tag className="w-3 h-3 mr-1" />
                {tag}
              </div>
            ))}

            {workflow.tags.length > 2 && (
              <span className="text-xs text-gray-400">+{workflow.tags.length - 2} more</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

const WorkflowDetailsModal: React.FC<{
  workflow: Workflow;
  onClose: () => void;
}> = ({ workflow, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-xl font-bold text-gray-900">{workflow.name}</h2>
              {workflow.priority && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    workflow.priority?.toUpperCase() === 'URGENT'
                      ? 'bg-red-100 text-red-800'
                      : workflow.priority?.toUpperCase() === 'HIGH'
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {workflow.priority}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600">{workflow.description}</p>
          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold ml-4"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Key Information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs text-gray-600 font-medium mb-1">Type</p>
              <p className="text-sm font-semibold text-gray-900">{workflow.type}</p>
            </div>

            {workflow.estimatedHours && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-600 font-medium mb-1">Estimated Hours</p>
                <p className="text-sm font-semibold text-gray-900">{workflow.estimatedHours}h</p>
              </div>
            )}

            {workflow.billing?.fixedAmount && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-600 font-medium mb-1">Fixed Amount</p>
                <p className="text-sm font-semibold text-gray-900">${workflow.billing.fixedAmount}</p>
              </div>
            )}

            {workflow.recurrence?.frequency && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-600 font-medium mb-1">Frequency</p>
                <p className="text-sm font-semibold text-gray-900">{workflow.recurrence.frequency}</p>
              </div>
            )}
          </div>

          {/* Tags */}
          {workflow.tags.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">Tags</p>
              <div className="flex flex-wrap gap-2">
                {workflow.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Checklist */}
          {workflow.checklist && workflow.checklist.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-3">Checklist Items</p>
              <div className="space-y-2">
                {workflow.checklist.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-5 h-5 rounded border-2 border-gray-300 flex-shrink-0 mt-0.5 flex items-center justify-center">
                      {item.required && <span className="w-2 h-2 bg-red-500 rounded-full"></span>}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{item.label}</p>
                      {item.description && (
                        <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
